import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { newsCategories, newsList } from "@/mocks/news";
import { formatDate } from "@/utils/media";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

const PAGE_SIZE = 4;
const LOAD_STEP = 2;

export default function News() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewMode, setViewMode] = useState<"page" | "more">("page");

  useSEO({
    title: "最新影视资讯 - 麻豆精品",
    description:
      "麻豆精品影视资讯频道，汇集电影新闻、电视剧动态与行业观察，为你提供及时、专业的影视资讯精选。",
    keywords: "麻豆精品,影视资讯,电影新闻,电视剧动态,行业观察",
  });

  const filtered = useMemo(
    () => (category === "全部" ? newsList : newsList.filter((item) => item.category === category)),
    [category],
  );

  useEffect(() => {
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const loadItems = filtered.slice(0, visibleCount);
  const items = viewMode === "page" ? pageItems : loadItems;

  const tabs = ["全部", ...newsCategories];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视资讯" }]} />

      <header className="mt-5">
        <h1 className="flex items-center gap-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-newspaper-line text-xl" aria-hidden="true"></i>
          </span>
          最新影视资讯
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          关注电影新闻、电视剧动态与行业观察，及时了解你感兴趣的影视世界。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setCategory(tab)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              category === tab
                ? "bg-primary-500 text-background-50"
                : "bg-background-200 text-foreground-700 hover:bg-background-300"
            }`}
          >
            {tab}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 rounded-full bg-background-200 p-1">
          <button
            type="button"
            onClick={() => setViewMode("page")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === "page" ? "bg-background-50 text-foreground-900" : "text-foreground-600"
            }`}
          >
            分页
          </button>
          <button
            type="button"
            onClick={() => setViewMode("more")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === "more" ? "bg-background-50 text-foreground-900" : "text-foreground-600"
            }`}
          >
            加载更多
          </button>
        </div>
      </div>

      {items.length > 0 ? (
        <div className="mt-6 flex flex-col gap-5">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group grid grid-cols-1 gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:grid-cols-[280px_1fr] md:gap-6"
            >
              <div className="aspect-[16/9] w-full overflow-hidden bg-background-200 sm:aspect-auto sm:h-full">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - 麻豆精品`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col p-4 pt-0 sm:py-5 sm:pr-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
                    {item.category}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {formatDate(item.publishedAt)}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-user-line" aria-hidden="true"></i>
                    {item.author}
                  </span>
                </div>
                <h2 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-xl">
                  {item.title}
                </h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
                <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-background-200 px-2 py-0.5 text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-6">
          <EmptyState
            icon="ri-newspaper-line"
            title="该分类暂无资讯"
            description="换个分类看看，或浏览全部影视资讯。"
            actionLabel="查看全部资讯"
            onAction={() => setCategory("全部")}
          />
        </div>
      )}

      {viewMode === "page" && items.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {viewMode === "more" && visibleCount < filtered.length && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((prev) => prev + LOAD_STEP)}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
          >
            <i className="ri-add-line" aria-hidden="true"></i>
            加载更多（剩余 {filtered.length - visibleCount} 篇）
          </button>
        </div>
      )}
    </div>
  );
}